import React, { useState, useEffect } from "react";
import ExpenseListing from "./ExpenseListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setTotalItems,
} from "src/redux/slices/ExpensesSlice";
import { useGetExpensesQuery } from "src/services/ExpensesServices";
import AddExpenseFormWrapper from "../Add/AddExpenseFormWrapper";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { RxPencil1 } from "react-icons/rx";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import EditExpenseFormWrapper from "../Edit/EditExpenseFormWrapper";
import { CircularProgress } from '@mui/material';
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const ExpenseListingWrapper = (props: Props) => {
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [expenseId, setExpenseId] = useState('');

  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isTableLoading,
    dateFilter,
  } = useSelector((state: RootState) => state.expenses);
  const [totalExpense, setTotalExpense] = useState([])

  const { isLoading, isFetching, data } = useGetExpensesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["accountLabel", "remark"],
    page: page,
    filterBy: [],
    dateFilter: {
      startDate: dateFilter?.start_date || "",
      endDate: dateFilter?.end_date || "",
    },
    orderBy: "createdAt",
    orderByValue: -1,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data?.[0]?.data || []));
      setTotalExpense((data?.data?.[0]));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
  }, [isLoading, isFetching, data, dispatch]);

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setIsOpenEditForm(true);
          setExpenseId(row?._id)
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.EXPENSES
      },

    ];
  };

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "expense_on_date",
      headerName: "Expense On Date",
      extraClasses:'justify-center     ',
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.expense_on_date, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.expense_on_date, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "accountLabel",
      extraClasses: "py-2",
      headerName: "Account Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.accountLabel} </span>,
    },
    {
      field: "branchName",
      headerName: "Branch Name - Code",
      extraClasses: "py-2 min-w-[150px]",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) =>
        <div className="flex flex-col ">
          <span> {row?.branchName} </span>
          <span className="text-[13px] font-medium text-slate-400">({row?.branchCode})</span>
        </div>,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> &#8377; {row?.amount} </span>,
    },
    {
      field: "expenceCategoryLabel",
      headerName: "Expence Category",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.expenceCategoryLabel} </span>,
    },
    {
      field: "createdByLabel",
      headerName: "Created By",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.createdByLabel} </span>,
    },
    {
      field: "remark",
      headerName: "Remark",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.remark} </span>,
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      {(isLoading || isFetching) && (
        <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
          <CircularProgress />
        </div>
      )}
      <ExpenseListing
        totalExpense={totalExpense}
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.EXPENSES,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddForm && (
        <AddExpenseFormWrapper onClose={() => setIsOpenAddForm(false)} />
      )}
      {isOpenEditForm && (
        <EditExpenseFormWrapper onClose={() => setIsOpenEditForm(false)} expenseId={expenseId} />
      )}
    </div>
  );
};

export default ExpenseListingWrapper;
